import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzColorPickerModule } from 'ng-zorro-antd/color-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import {
  ISablon, ISablonElement, ISablonGorunumStil, IArkaPlan, HAZIR_ARKAPLANLAR,
} from '../sablon-editoru/sablon.interface';

type ISablonArkaPlan = IArkaPlan;

const TEMALAR: { anahtar: string; baslik: string; stil: Partial<ISablonGorunumStil> }[] = [
  { anahtar: 'koyu', baslik: 'Koyu', stil: { gradyan: 'linear-gradient(135deg, #0d1b2a, #1e293b)', renkYazi: '#e0e7ff', arkaPlan: '#0f172a', golge: '0 4px 16px rgba(0,0,0,0.4)' } },
  { anahtar: 'acik', baslik: 'Acik', stil: { gradyan: 'linear-gradient(135deg, #ffffff, #f5f7fa)', renkYazi: '#262626', arkaPlan: '#ffffff', golge: '0 2px 8px rgba(0,0,0,0.08)' } },
  { anahtar: 'okyanus', baslik: 'Okyanus', stil: { gradyan: 'linear-gradient(135deg, #0077b6, #00b4d8, #90e0ef)', renkYazi: '#ffffff', arkaPlan: '#0077b6', golge: '0 4px 16px rgba(0,119,182,0.3)' } },
  { anahtar: 'orman', baslik: 'Orman', stil: { gradyan: 'linear-gradient(135deg, #064e3b, #065f46, #10b981)', renkYazi: '#ffffff', arkaPlan: '#064e3b', golge: '0 4px 16px rgba(16,185,129,0.3)' } },
  { anahtar: 'gunbatimi', baslik: 'Gun Batimi', stil: { gradyan: 'linear-gradient(135deg, #f12711, #f5af19)', renkYazi: '#ffffff', arkaPlan: '#f12711', golge: '0 4px 16px rgba(241,39,17,0.3)' } },
  { anahtar: 'gul', baslik: 'Gul', stil: { gradyan: 'linear-gradient(135deg, #fbc2eb, #a6c1ee)', renkYazi: '#4a1942', arkaPlan: '#fbc2eb', golge: '0 4px 16px rgba(251,194,235,0.4)' } },
];

const GOLGELER: { baslik: string; deger: string }[] = [
  { baslik: 'Yok', deger: '' },
  { baslik: 'Hafif', deger: '0 2px 8px rgba(0,0,0,0.15)' },
  { baslik: 'Orta', deger: '0 4px 16px rgba(0,0,0,0.25)' },
  { baslik: 'Guclu', deger: '0 8px 32px rgba(0,0,0,0.4)' },
  { baslik: 'Neon Mavi', deger: '0 0 20px rgba(24,144,255,0.3)' },
  { baslik: 'Neon Yesil', deger: '0 0 20px rgba(82,196,26,0.3)' },
  { baslik: 'Neon Kirmizi', deger: '0 0 20px rgba(245,34,45,0.3)' },
];

@Component({
  selector: 'te-stil-paneli',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    NzInputModule, NzSliderModule, NzSelectModule,
    NzInputNumberModule, NzColorPickerModule, NzButtonModule, NzSegmentedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (seciliElement(); as el) {
      <div class="sp-bolum">
        <div class="sp-baslik">Boyut</div>
        <div class="sp-grid2">
          <div class="sp-alan">
            <span class="sp-etiket">G (%)</span>
            <nz-input-number nzSize="small" [nzMin]="1" [nzMax]="100" [nzStep]="1"
              [ngModel]="el.genislik" (ngModelChange)="elGuncelle({ genislik: $event })" />
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Y (%)</span>
            <nz-input-number nzSize="small" [nzMin]="1" [nzMax]="100" [nzStep]="1"
              [ngModel]="el.yukseklik" (ngModelChange)="elGuncelle({ yukseklik: $event })" />
          </div>
        </div>
      </div>

      <div class="sp-bolum">
        <div class="sp-baslik">Pozisyon</div>
        <div class="sp-grid2">
          <div class="sp-alan">
            <span class="sp-etiket">X (%)</span>
            <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="100" [nzStep]="1"
              [ngModel]="el.x" (ngModelChange)="elGuncelle({ x: $event })" />
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Y (%)</span>
            <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="100" [nzStep]="1"
              [ngModel]="el.y" (ngModelChange)="elGuncelle({ y: $event })" />
          </div>
        </div>
      </div>

      @if (metinTuru(el)) {
        <div class="sp-bolum">
          <div class="sp-baslik">Yazi</div>
          <div class="sp-grid2">
            <div class="sp-alan">
              <span class="sp-etiket">Boyut (px)</span>
              <nz-input-number nzSize="small" [nzMin]="6" [nzMax]="120" [nzStep]="1"
                [ngModel]="el.stil.fontSize ?? 14" (ngModelChange)="stilGuncelle('fontSize', $event)" />
            </div>
            <div class="sp-alan">
              <span class="sp-etiket">Kalinlik</span>
              <nz-select nzSize="small" [ngModel]="el.stil.fontWeight ?? '400'" (ngModelChange)="stilGuncelle('fontWeight', $event)" style="width:100%">
                <nz-option nzValue="300" nzLabel="Ince" />
                <nz-option nzValue="400" nzLabel="Normal" />
                <nz-option nzValue="500" nzLabel="Orta" />
                <nz-option nzValue="600" nzLabel="Yari Kalin" />
                <nz-option nzValue="700" nzLabel="Kalin" />
                <nz-option nzValue="800" nzLabel="Ekstra Kalin" />
                <nz-option nzValue="900" nzLabel="Siyah" />
              </nz-select>
            </div>
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Font Ailesi</span>
            <input nz-input nzSize="small" [ngModel]="el.stil.fontFamily ?? ''" (ngModelChange)="stilGuncelle('fontFamily', $event)" placeholder="Inter, system-ui" />
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Renk</span>
            <div class="sp-renk-satir">
              <nz-color-picker nzSize="small" [nzValue]="el.stil.color ?? '#ffffff'" (nzOnChange)="stilGuncelle('color', $event.color.toHexString())" />
              <input nz-input nzSize="small" [ngModel]="el.stil.color ?? '#ffffff'" (ngModelChange)="stilGuncelle('color', $event)" class="sp-renk-input" />
            </div>
          </div>
          <div class="sp-grid2">
            <div class="sp-alan">
              <span class="sp-etiket">Harf Araligi</span>
              <nz-input-number nzSize="small" [nzMin]="-5" [nzMax]="20" [nzStep]="0.5"
                [ngModel]="el.stil.letterSpacing ?? 0" (ngModelChange)="stilGuncelle('letterSpacing', $event)" />
            </div>
            <div class="sp-alan">
              <span class="sp-etiket">Satir Yuksekligi</span>
              <nz-input-number nzSize="small" [nzMin]="0.5" [nzMax]="4" [nzStep]="0.1"
                [ngModel]="el.stil.lineHeight ?? 1.4" (ngModelChange)="stilGuncelle('lineHeight', $event)" />
            </div>
          </div>
        </div>
      }

      @if (el.tur === 'sekil') {
        <div class="sp-bolum">
          <div class="sp-baslik">Sekil</div>
          <div class="sp-alan">
            <span class="sp-etiket">Dolgu</span>
            <div class="sp-renk-satir">
              <nz-color-picker nzSize="small" [nzValue]="el.stil.fill ?? '#cccccc'" (nzOnChange)="stilGuncelle('fill', $event.color.toHexString())" />
              <input nz-input nzSize="small" [ngModel]="el.stil.fill ?? '#cccccc'" (ngModelChange)="stilGuncelle('fill', $event)" class="sp-renk-input" />
            </div>
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Cizgi Rengi</span>
            <div class="sp-renk-satir">
              <nz-color-picker nzSize="small" [nzValue]="el.stil.stroke ?? '#000000'" (nzOnChange)="stilGuncelle('stroke', $event.color.toHexString())" />
              <input nz-input nzSize="small" [ngModel]="el.stil.stroke ?? '#000000'" (ngModelChange)="stilGuncelle('stroke', $event)" class="sp-renk-input" />
            </div>
          </div>
          <div class="sp-grid2">
            <div class="sp-alan">
              <span class="sp-etiket">Cizgi Kalinligi</span>
              <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="20" [nzStep]="1"
                [ngModel]="el.stil.strokeWidth ?? 0" (ngModelChange)="stilGuncelle('strokeWidth', $event)" />
            </div>
            <div class="sp-alan">
              <span class="sp-etiket">Kose Yuvarlak</span>
              <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="100" [nzStep]="1"
                [ngModel]="el.stil.borderRadius ?? 0" (ngModelChange)="stilGuncelle('borderRadius', $event)" />
            </div>
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Saydamlik</span>
            <nz-slider [nzMin]="0" [nzMax]="1" [nzStep]="0.05"
              [ngModel]="el.stil.opacity ?? 1" (ngModelChange)="stilGuncelle('opacity', $event)" />
          </div>
        </div>
      }

      <div class="sp-bolum">
        <div class="sp-baslik">Animasyon</div>
        <div class="sp-alan">
          <span class="sp-etiket">Giris</span>
          <nz-select nzSize="small" nzPlaceHolder="Yok" nzAllowClear
            [ngModel]="el.animasyon?.giris ?? null" (ngModelChange)="animGuncelle('giris', $event)" style="width:100%">
            <nz-option nzValue="fadeIn" nzLabel="Belirme" />
            <nz-option nzValue="slideUp" nzLabel="Asagidan" />
            <nz-option nzValue="slideDown" nzLabel="Yukaridan" />
            <nz-option nzValue="slideLeft" nzLabel="Sagdan" />
            <nz-option nzValue="slideRight" nzLabel="Soldan" />
            <nz-option nzValue="scaleIn" nzLabel="Buyuyerek" />
            <nz-option nzValue="bounce" nzLabel="Ziplayarak" />
            <nz-option nzValue="flip" nzLabel="Donerek" />
            <nz-option nzValue="rotateIn" nzLabel="Cevirerek" />
          </nz-select>
        </div>
        <div class="sp-alan">
          <span class="sp-etiket">Surekli</span>
          <nz-select nzSize="small" nzPlaceHolder="Yok" nzAllowClear
            [ngModel]="el.animasyon?.surekli ?? null" (ngModelChange)="animGuncelle('surekli', $event)" style="width:100%">
            <nz-option nzValue="pulse" nzLabel="Nabiz" />
            <nz-option nzValue="float" nzLabel="Suzulme" />
            <nz-option nzValue="rotate" nzLabel="Donme" />
            <nz-option nzValue="shimmer" nzLabel="Parildama" />
            <nz-option nzValue="blink" nzLabel="Yanip sonme" />
            <nz-option nzValue="shake" nzLabel="Titreme" />
            <nz-option nzValue="swing" nzLabel="Sallanma" />
            <nz-option nzValue="breathe" nzLabel="Nefes" />
            <nz-option nzValue="glow" nzLabel="Parlama" />
          </nz-select>
        </div>
        <div class="sp-alan">
          <span class="sp-etiket">Hover</span>
          <nz-select nzSize="small" nzPlaceHolder="Yok" nzAllowClear
            [ngModel]="el.animasyon?.hover ?? null" (ngModelChange)="animGuncelle('hover', $event)" style="width:100%">
            <nz-option nzValue="scale" nzLabel="Buyume" />
            <nz-option nzValue="glow" nzLabel="Parlama" />
            <nz-option nzValue="shake" nzLabel="Titreme" />
            <nz-option nzValue="jello" nzLabel="Jole" />
            <nz-option nzValue="lift" nzLabel="Kalkma" />
            <nz-option nzValue="colorShift" nzLabel="Renk kaydir" />
          </nz-select>
        </div>
        <div class="sp-alan">
          <span class="sp-etiket">Tiklama</span>
          <nz-select nzSize="small" nzPlaceHolder="Yok" nzAllowClear
            [ngModel]="el.animasyon?.tiklama ?? null" (ngModelChange)="animGuncelle('tiklama', $event)" style="width:100%">
            <nz-option nzValue="pop" nzLabel="Pop" />
            <nz-option nzValue="ripple" nzLabel="Dalga" />
            <nz-option nzValue="shrink" nzLabel="Kuculme" />
            <nz-option nzValue="flash" nzLabel="Flas" />
            <nz-option nzValue="rubber" nzLabel="Lastik" />
            <nz-option nzValue="tada" nzLabel="Tada" />
            <nz-option nzValue="heartbeat" nzLabel="Kalp atisi" />
          </nz-select>
        </div>
        @if (el.animasyon?.giris) {
          <div class="sp-alan">
            <span class="sp-etiket">Giris Gecikme (ms)</span>
            <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="5000" [nzStep]="100"
              [ngModel]="el.animasyon?.girisGecikme ?? 0" (ngModelChange)="animGuncelle('girisGecikme', $event)" />
          </div>
        }
        @if (el.animasyon?.surekli) {
          <div class="sp-alan">
            <span class="sp-etiket">Surekli Sure (ms)</span>
            <nz-input-number nzSize="small" [nzMin]="100" [nzMax]="10000" [nzStep]="100"
              [ngModel]="el.animasyon?.surekliSure ?? 1000" (ngModelChange)="animGuncelle('surekliSure', $event)" />
          </div>
        }
      </div>

      <div class="sp-bolum">
        <div class="sp-baslik">Hizalama</div>
        <div class="sp-hiza-satir">
          <button nz-button nzSize="small" nzType="text" (click)="hizala('sol')">Sol</button>
          <button nz-button nzSize="small" nzType="text" (click)="hizala('sag')">Sag</button>
          <button nz-button nzSize="small" nzType="text" (click)="hizala('ust')">Ust</button>
          <button nz-button nzSize="small" nzType="text" (click)="hizala('alt')">Alt</button>
          <button nz-button nzSize="small" nzType="text" (click)="hizala('yatayOrta')">OrtaY</button>
          <button nz-button nzSize="small" nzType="text" (click)="hizala('dikeyOrta')">OrtaX</button>
        </div>
        <div class="sp-hiza-satir">
          <button nz-button nzSize="small" nzType="text" (click)="sigdir('yatay')">Yatay Sigdir</button>
          <button nz-button nzSize="small" nzType="text" (click)="sigdir('dikey')">Dikey Sigdir</button>
        </div>
      </div>

      <div class="sp-bolum">
        <div class="sp-baslik">Katman</div>
        <div class="sp-alan">
          <nz-input-number nzSize="small" [nzMin]="0" [nzMax]="100" [nzStep]="1"
            [ngModel]="el.katman ?? 0" (ngModelChange)="elGuncelle({ katman: $event })" />
        </div>
      </div>

    } @else {
      @if (sablon(); as s) {
        <div class="sp-bolum">
          <div class="sp-baslik">Arkaplan</div>
          <div class="sp-arkaplan-grid">
            @for (ap of hazirArkaplanlar; track ap.anahtar) {
              <div class="sp-arkaplan-tile"
                [class.aktif]="s.arkaPlan?.deger === ap.arkaPlan?.deger"
                [style.background]="ap.arkaPlan.deger"
                [title]="ap.baslik"
                (click)="arkaPlanSec(ap.arkaPlan)">
              </div>
            }
          </div>
          <div class="sp-alan">
            <span class="sp-etiket">Ozel Renk</span>
            <div class="sp-renk-satir">
              <nz-color-picker nzSize="small" [nzValue]="s.arkaPlan?.deger || '#ffffff'" (nzOnChange)="arkaPlanSec({ tur: 'renk', deger: $event.color.toHexString() })" />
              <input nz-input nzSize="small" placeholder="CSS gradient..." (change)="arkaPlanSec({ tur: 'gradient', deger: $any($event.target).value })" class="sp-renk-input" />
            </div>
          </div>
        </div>

        <div class="sp-bolum">
          <div class="sp-baslik">Tema</div>
          <div class="sp-tema-grid">
            @for (t of temalar; track t.anahtar) {
              <button class="sp-tema-btn" (click)="temaUygula(t)">
                <span class="sp-tema-onizleme" [style.background]="t.stil.gradyan"></span>
                <span>{{ t.baslik }}</span>
              </button>
            }
          </div>
        </div>

        <div class="sp-bolum">
          <div class="sp-baslik">Golge</div>
          <div class="sp-golge-grid">
            @for (g of golgeler; track g.baslik) {
              <button class="sp-golge-btn"
                [class.aktif]="(s.stil?.golge ?? '') === g.deger"
                (click)="sablonStilGuncellendi.emit({ golge: g.deger })">
                {{ g.baslik }}
              </button>
            }
          </div>
        </div>

        <div class="sp-bolum">
          <div class="sp-baslik">Kose Yuvarlak</div>
          <nz-slider [nzMin]="0" [nzMax]="32" [nzStep]="1"
            [ngModel]="s.stil?.borderRadius ?? 8" (ngModelChange)="sablonStilGuncellendi.emit({ borderRadius: $event })" />
        </div>
      }
    }
  `,
  styles: `
    :host {
      display: block;
      padding: 10px;
      overflow-y: auto;
      font-size: 12px;
    }
    .sp-bolum { margin-bottom: 14px; }
    .sp-baslik {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: var(--te-label, rgba(0,0,0,0.45));
      margin-bottom: 6px;
      font-weight: 600;
    }
    .sp-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
    .sp-alan { margin-bottom: 6px; }
    .sp-alan nz-input-number, .sp-alan nz-select, .sp-alan input[nz-input] { width: 100%; }
    .sp-etiket {
      display: block;
      font-size: 10px;
      color: var(--te-text-secondary, rgba(0,0,0,0.5));
      margin-bottom: 2px;
    }
    .sp-renk-satir { display: flex; align-items: center; gap: 6px; }
    .sp-renk-input { flex: 1; }
    .sp-hiza-satir { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 4px; }
    .sp-hiza-satir button { font-size: 10px; padding: 0 6px; height: 24px; }
    .sp-arkaplan-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 8px; }
    .sp-arkaplan-tile {
      height: 32px; border-radius: 4px; cursor: pointer;
      border: 2px solid transparent;
      transition: border-color 0.15s, transform 0.15s;
    }
    .sp-arkaplan-tile:hover { transform: scale(1.08); }
    .sp-arkaplan-tile.aktif { border-color: #1890ff; }
    .sp-tema-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
    .sp-tema-btn {
      display: flex; flex-direction: column; align-items: center; gap: 3px;
      padding: 6px 4px;
      border: 1px solid var(--te-btn-border, rgba(0,0,0,0.12));
      border-radius: 6px;
      background: var(--te-btn-bg, rgba(0,0,0,0.03));
      color: var(--te-text-secondary, rgba(0,0,0,0.55));
      cursor: pointer; font-size: 10px;
      transition: background 0.15s, border-color 0.15s;
    }
    .sp-tema-btn:hover {
      background: var(--te-btn-bg-hover, rgba(0,0,0,0.06));
      border-color: var(--te-btn-border-hover, rgba(0,0,0,0.2));
    }
    .sp-tema-onizleme { width: 100%; height: 18px; border-radius: 3px; }
    .sp-golge-grid { display: flex; flex-wrap: wrap; gap: 4px; }
    .sp-golge-btn {
      padding: 3px 8px;
      border: 1px solid var(--te-btn-border, rgba(0,0,0,0.12));
      border-radius: 4px;
      background: var(--te-btn-bg, rgba(0,0,0,0.03));
      color: var(--te-text-secondary, rgba(0,0,0,0.55));
      cursor: pointer; font-size: 10px;
      transition: background 0.15s, border-color 0.15s;
    }
    .sp-golge-btn:hover { background: var(--te-btn-bg-hover, rgba(0,0,0,0.06)); }
    .sp-golge-btn.aktif { border-color: #1890ff; background: rgba(24,144,255,0.12); }

    :host-context(body.tema-karanlik) {
      --te-label: rgba(255,255,255,0.45);
      --te-text-secondary: rgba(255,255,255,0.6);
      --te-btn-border: rgba(255,255,255,0.1);
      --te-btn-bg: rgba(255,255,255,0.04);
      --te-btn-bg-hover: rgba(255,255,255,0.08);
      --te-btn-border-hover: rgba(255,255,255,0.2);
    }
  `,
})
export class TeStilPaneliComponent {
  seciliElement = input<ISablonElement | null>(null);
  sablon = input<ISablon>();

  elementGuncellendi = output<Partial<ISablonElement>>();
  sablonStilGuncellendi = output<Partial<ISablonGorunumStil>>();
  sablonArkaPlanGuncellendi = output<Partial<ISablonArkaPlan>>();

  readonly hazirArkaplanlar = HAZIR_ARKAPLANLAR;
  readonly temalar = TEMALAR;
  readonly golgeler = GOLGELER;

  metinTuru(el: ISablonElement): boolean {
    return el.tur === 'sabit-metin' || el.tur === 'parametre-metin'
      || el.tur === 'parametre-render' || el.tur === 'markdown';
  }

  elGuncelle(degisiklik: Partial<ISablonElement>) {
    this.elementGuncellendi.emit(degisiklik);
  }

  stilGuncelle(key: string, value: any) {
    const el = this.seciliElement();
    if (!el) return;
    this.elementGuncellendi.emit({ stil: { ...el.stil, [key]: value } });
  }

  animGuncelle(alan: string, deger: any) {
    const el = this.seciliElement();
    if (!el) return;
    const animasyon = { ...(el.animasyon ?? {}) } as any;
    if (deger == null) {
      delete animasyon[alan];
    } else {
      animasyon[alan] = deger;
    }
    this.elementGuncellendi.emit({
      animasyon: Object.keys(animasyon).length ? animasyon : undefined,
    });
  }

  hizala(yon: string) {
    const degisiklikler: Partial<ISablonElement> = {};
    switch (yon) {
      case 'sol': degisiklikler.x = 0; break;
      case 'sag': degisiklikler.x = 100; break;
      case 'ust': degisiklikler.y = 0; break;
      case 'alt': degisiklikler.y = 100; break;
      case 'yatayOrta': degisiklikler.x = 50; break;
      case 'dikeyOrta': degisiklikler.y = 50; break;
    }
    this.elementGuncellendi.emit(degisiklikler);
  }

  sigdir(mod: string) {
    switch (mod) {
      case 'yatay': this.elementGuncellendi.emit({ x: 0, genislik: 100 }); break;
      case 'dikey': this.elementGuncellendi.emit({ y: 0, yukseklik: 100 }); break;
    }
  }

  arkaPlanSec(ap: IArkaPlan) {
    this.sablonArkaPlanGuncellendi.emit(ap);
  }

  temaUygula(t: typeof TEMALAR[0]) {
    this.sablonStilGuncellendi.emit(t.stil);
  }
}
